import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import BookCallModal from './BookCallModal';
import { 
  Search, 
  Briefcase, 
  TrendingUp, 
  Users, 
  Target, 
  Zap,
  Shield,
  BarChart3,
  PlayCircle,
  ArrowRight,
  CheckCircle,
  Star,
  Smartphone,
  MessageCircle,
  Calendar,
  FileText,
  Download,
  Award,
  Apple
} from 'lucide-react';

// Removed distracting floating particles for better UX focus

// Phone mockup component with enhanced 3D effects (no multiple shadows)
const PhoneMockup = ({ children, className = "" }) => {
  return (
    <div className={`relative ${className}`}>
      {/* Main phone with enhanced 3D styling */}
      <div className="relative mx-auto w-72 h-[580px] bg-gradient-to-b from-gray-800 to-gray-900 rounded-[3rem] p-2 transform perspective-1000 rotate-y-5 hover:rotate-y-0 transition-all duration-700">
        {/* Phone outer frame with metallic effect */}
        <div className="w-full h-full bg-gradient-to-b from-gray-900 via-black to-gray-900 rounded-[2.5rem] relative overflow-hidden shadow-inner">
          {/* Screen with subtle glow */}
          <div className="absolute inset-2 bg-white rounded-[2rem] overflow-hidden shadow-lg ring-1 ring-gray-200">
            {/* Screen glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-purple-50/30 pointer-events-none"></div>
            
            {/* Notch with enhanced styling */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-gradient-to-b from-black to-gray-900 rounded-b-2xl z-10 shadow-lg">
              {/* Camera and speaker */}
              <div className="absolute top-2 left-1/2 transform -translate-x-1/2 flex items-center space-x-3">
                <div className="w-1.5 h-1.5 bg-gray-700 rounded-full"></div>
                <div className="w-8 h-1 bg-gray-800 rounded-full"></div>
                <div className="w-1.5 h-1.5 bg-gray-700 rounded-full"></div>
              </div>
            </div>
            
            {/* Content */}
            <div className="pt-8 h-full overflow-hidden relative z-0">
              {children}
            </div>
          </div>
          
          {/* Enhanced home indicator */}
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-gray-300 via-white to-gray-300 rounded-full shadow-sm"></div>
          
          {/* Side buttons */}
          <div className="absolute left-0 top-24 w-1 h-8 bg-gradient-to-b from-gray-700 to-gray-800 rounded-r-lg"></div>
          <div className="absolute left-0 top-36 w-1 h-12 bg-gradient-to-b from-gray-700 to-gray-800 rounded-r-lg"></div>
          <div className="absolute left-0 top-52 w-1 h-12 bg-gradient-to-b from-gray-700 to-gray-800 rounded-r-lg"></div>
          <div className="absolute right-0 top-32 w-1 h-16 bg-gradient-to-b from-gray-700 to-gray-800 rounded-l-lg"></div>
        </div>
        
        {/* Reflection effect */}
        <div className="absolute inset-2 bg-gradient-to-tr from-white/10 via-transparent to-transparent rounded-[2rem] pointer-events-none"></div>
      </div>
    </div>
  );
};

const ShowcaseLanding = () => {
  const [isVisible, setIsVisible] = useState({});
  const [currentPhoneScreen, setCurrentPhoneScreen] = useState(0);
  const [showBookCallModal, setShowBookCallModal] = useState(false);

  // Phone screens rotation
  const phoneScreens = [
    {
      title: "Dashboard",
      content: (
        <div className="p-6 h-full bg-gradient-to-b from-blue-50 to-white">
          <div className="flex items-center justify-between mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_agents-connect/artifacts/f4wofrcv_AGENT_ADVANCER_senza_sfondo_-removebg-preview.png"
              alt="AgentsPlus"
              className="h-8 w-auto"
            />
            <div className="w-8 h-8 bg-gradient-to-br from-[#2563EB] to-[#7C3AED] rounded-full"></div>
          </div>
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                <span className="text-sm font-medium">5 Nuovi Match</span>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center space-x-3">
                <Calendar className="h-4 w-4 text-[#2563EB]" />
                <span className="text-sm">3 Colloqui questa settimana</span>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center space-x-3">
                <BarChart3 className="h-4 w-4 text-[#7C3AED]" />
                <span className="text-sm">I tuoi risultati migliorano</span>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Agenzie",
      content: (
        <div className="p-6 h-full bg-white">
          <h3 className="font-semibold text-gray-800 mb-4">Agenzie Consigliate</h3>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm text-gray-800">Immobiliare {i === 1 ? 'Milano Centro' : i === 2 ? 'Roma Nord' : 'Torino Sud'}</div>
                    <div className="text-xs text-gray-500">Match: {90 + i}%</div>
                  </div>
                  <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      title: "Chat",
      content: (
        <div className="p-6 h-full bg-white">
          <h3 className="font-semibold text-gray-800 mb-4">Messaggi</h3>
          <div className="space-y-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-xs text-[#2563EB] font-medium mb-1">Immobiliare Milano</div>
              <div className="text-sm text-gray-700">Perfetto! Quando puoi per un colloquio?</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg ml-8">
              <div className="text-sm text-gray-700">Disponibile domani alle 15:00</div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-xs text-[#2563EB] font-medium mb-1">Immobiliare Milano</div>
              <div className="text-sm text-gray-700">Perfetto, ti aspettiamo! 📍</div>
            </div>
          </div>
        </div>
      )
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[id]').forEach((el) => {
      observer.observe(el);
    });

    // Phone screen rotation
    const interval = setInterval(() => {
      setCurrentPhoneScreen((prev) => (prev + 1) % phoneScreens.length);
    }, 4000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ 
      behavior: 'smooth' 
    });
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Custom CSS for animations and 3D effects */}
      <style jsx>{`
        @keyframes wave {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100vw); }
        }
        .animate-wave {
          animation: wave 8s linear infinite;
        }
        
        .perspective-1000 {
          perspective: 1000px;
        }
        
        .rotate-y-5 {
          transform: rotateY(5deg) rotateX(5deg);
        }
        
        .rotate-y-0 {
          transform: rotateY(0deg) rotateX(0deg);
        }
        
        /* Enhanced phone 3D hover effects */
        .phone-3d {
          transition: all 0.7s cubic-bezier(0.23, 1, 0.320, 1);
          transform-style: preserve-3d;
        }
        
        .phone-3d:hover {
          transform: rotateY(-5deg) rotateX(-2deg) translateY(-10px);
        }
        
        /* Floating animation for phones */
        @keyframes phoneFloat {
          0%, 100% { 
            transform: rotateY(5deg) rotateX(5deg) translateY(0px);
          }
          50% { 
            transform: rotateY(5deg) rotateX(5deg) translateY(-8px);
          }
        }
        
        .animate-phone-float {
          animation: phoneFloat 4s ease-in-out infinite;
        }
      `}</style>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src="/logo-originale.png"
              alt="InLeads Logo"
              className="h-8 w-auto"
            />
            <span className="text-xl font-bold text-gray-900">InLeads</span>
          </div>
          
          <nav className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('features')} 
              className="text-gray-700 hover:text-[#2563EB] transition-colors duration-200"
            >
              Funzionalità
            </button>
            <button 
              onClick={() => scrollToSection('tutorial')} 
              className="text-gray-700 hover:text-[#2563EB] transition-colors duration-200"
            >
              Come funziona
            </button>
            <a 
              href="/blog" 
              className="text-gray-700 hover:text-[#2563EB] transition-colors duration-200"
            >
              Blog
            </a>
            <a 
              href="/contatti" 
              className="text-gray-700 hover:text-[#2563EB] transition-colors duration-200"
            >
              Contatti
            </a>
            <Button
              asChild
              className="bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white px-4 py-2 rounded-lg font-semibold hover:shadow-lg transition-all duration-200 flex items-center"
            >
              <a
                href={`https://calendly.com/inleads-info/30min?month=${new Date().toISOString().slice(0,7)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Prenota una consulenza
              </a>
            </Button>
            <Button 
              asChild 
              className="bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white px-6 py-2 rounded-lg hover:shadow-lg transition-all duration-200"
            >
              <a href="https://app.inleads.it/#/accesso" target="_blank" rel="noopener noreferrer">
                Prova l'App
              </a>
            </Button>
          </nav>

          {/* Mobile menu button */}
          <Button variant="ghost" className="md:hidden">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </Button>
        </div>
      </header>

      {/* Hero Section with Smartphone Mockup */}
      <section 
        id="hero" 
        className="relative pt-24 pb-20 px-6 min-h-screen flex items-center bg-gradient-to-br from-blue-50 via-white to-purple-50 overflow-hidden"
      >
        
        {/* Background waves */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-0 left-0 w-full overflow-hidden">
            <svg className="relative block w-full h-32" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
              <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" className="fill-[#2563EB]"></path>
            </svg>
          </div>
        </div>

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className={`space-y-8 transition-all duration-1000 ${isVisible.hero ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <div>
                <Badge className="mb-4 bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20 px-4 py-2">
                  Tecnologia + Consulenza Umana
                </Badge>
                <h1 className="text-4xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">
                  Scopri il modo più{' '}
                  <span className="bg-gradient-to-r from-[#2563EB] to-[#7C3AED] bg-clip-text text-transparent">
                    intelligente
                  </span>
                  {' '}per connettere agenzie e candidati
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed mb-8">
                  Con una breve consulenza gratuita, ti mostriamo come entrare nel mondo InLeads.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 w-full max-w-3xl mx-auto">
                <Button 
                  onClick={() => scrollToSection('candidati')}
                  size="lg" 
                  className="bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white px-4 sm:px-6 py-3 sm:py-4 rounded-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 group w-full sm:flex-1 min-h-[70px] sm:min-h-[80px] flex items-center justify-center"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="flex items-center text-sm sm:text-base font-semibold">
                      <Users className="mr-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:scale-110 transition-transform duration-200" />
                      Sono un Candidato
                    </div>
                    <div className="text-xs sm:text-sm opacity-90 mt-1 leading-tight">Scopri la tua agenzia</div>
                  </div>
                </Button>
                <Button 
                  onClick={() => scrollToSection('agenzie')}
                  size="lg" 
                  variant="outline"
                  className="border-2 border-[#2563EB] text-[#2563EB] px-4 sm:px-6 py-3 sm:py-4 rounded-xl hover:bg-[#2563EB] hover:text-white transition-all duration-300 transform hover:-translate-y-1 group w-full sm:flex-1 min-h-[70px] sm:min-h-[80px] flex items-center justify-center"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="flex items-center text-sm sm:text-base font-semibold">
                      <Briefcase className="mr-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:scale-110 transition-transform duration-200" />
                      Sono un'Agenzia
                    </div>
                    <div className="text-xs sm:text-sm opacity-90 mt-1 leading-tight">Richiedi demo gratuita</div>
                  </div>
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#2563EB]">2.500+</div>
                  <div className="text-sm text-gray-600">Candidati attivi</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#7C3AED]">150+</div>
                  <div className="text-sm text-gray-600">Agenzie partner</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-500">95%</div>
                  <div className="text-sm text-gray-600">Match di successo</div>
                </div>
              </div>
            </div>

            {/* Right content - Phone mockup */}
            <div className={`flex justify-center lg:justify-end transition-all duration-1000 delay-300 ${isVisible.hero ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <PhoneMockup className="phone-3d hover:scale-105">
                {phoneScreens[currentPhoneScreen].content}
              </PhoneMockup>
            </div>
          </div>
        </div>
      </section>

      {/* Step-by-Step Tutorial Section */}
      <section 
        id="tutorial" 
        className={`py-20 px-6 bg-gray-50 transition-all duration-1000 ${isVisible.tutorial ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20">
              Semplicissimo da usare
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Come iniziare in 3 semplici passi
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Un processo veloce e personalizzato per entrare nel mondo InLeads
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Step 1 */}
            <div className="group relative">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#2563EB] to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 relative group-hover:scale-110 transition-all duration-300 shadow-lg">
                  <span className="text-2xl font-bold text-white">1</span>
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                    <FileText className="h-3 w-3 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#2563EB] transition-colors duration-300">
                  Ti prenoti in 30 secondi
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Scegli il giorno e l'orario che preferisci per una breve chiamata conoscitiva
                </p>
              </div>
              {/* Connection line */}
              <div className="hidden md:block absolute top-10 left-full w-8 h-0.5 bg-gradient-to-r from-[#2563EB] to-[#7C3AED] transform translate-x-4"></div>
            </div>

            {/* Step 2 */}
            <div className="group relative">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#7C3AED] to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 relative group-hover:scale-110 transition-all duration-300 shadow-lg">
                  <span className="text-2xl font-bold text-white">2</span>
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                    <Search className="h-3 w-3 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#7C3AED] transition-colors duration-300">
                  Ti contatta un membro del nostro team
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Una consulenza personalizzata per capire i tuoi obiettivi e presentarti la piattaforma
                </p>
              </div>
              {/* Connection line */}
              <div className="hidden md:block absolute top-10 left-full w-8 h-0.5 bg-gradient-to-r from-[#7C3AED] to-green-500 transform translate-x-4"></div>
            </div>

            {/* Step 3 */}
            <div className="group">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 relative group-hover:scale-110 transition-all duration-300 shadow-lg">
                  <span className="text-2xl font-bold text-white">3</span>
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center">
                    <Calendar className="h-3 w-3 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-green-600 transition-colors duration-300">
                  Ti attiviamo sull'app
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Accesso immediato alla piattaforma con profilo pronto e tutto impostato per il successo
                </p>
              </div>
            </div>
          </div>

          {/* CTA for tutorial */}
          <div className="text-center">
            <Button 
              onClick={() => scrollToSection('features')}
              size="lg" 
              className="bg-gradient-to-r from-green-500 to-[#2563EB] text-white px-10 py-4 text-lg rounded-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
            >
              Inizia subito il processo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Call Strategica Section */}
      <section 
        id="call-strategica"
        className={`py-20 px-6 bg-gradient-to-br from-[#2563EB] via-[#7C3AED] to-purple-700 text-white relative overflow-hidden transition-all duration-1000 ${isVisible['call-strategica'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="container mx-auto max-w-5xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-white/20 text-white border-white/30">
                Call Strategica Gratuita
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
                📅 Prenota la tua Call Strategica con InLeads
              </h2>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Scopri come InLeads può rivoluzionare il reclutamento della tua agenzia. 
                30 minuti di consulenza personalizzata con i nostri esperti.
              </p>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-300 mt-0.5 flex-shrink-0" />
                  <span className="text-blue-100">Analisi personalizzata delle tue esigenze di reclutamento</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-300 mt-0.5 flex-shrink-0" />
                  <span className="text-blue-100">Presentazione dei pacchetti più adatti alla tua agenzia</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-300 mt-0.5 flex-shrink-0" />
                  <span className="text-blue-100">Strategie comprovate per migliorare la retention del team</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-300 mt-0.5 flex-shrink-0" />
                  <span className="text-blue-100">Q&A personalizzato con i nostri esperti</span>
                </li>
              </ul>
              
              <Button 
                asChild
                size="lg"
                className="bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white px-8 py-4 text-lg rounded-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 font-bold"
              >
                <a
                  href={`https://calendly.com/inleads-info/30min?month=${new Date().toISOString().slice(0,7)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Prenota una consulenza
                </a>
              </Button>
              <p className="text-sm text-green-200 mt-3">✅ Risposta Immediata • ⏰ Supporto Rapido • 🎯 Assistenza Personalizzata</p>
            </div>

            <div className="hidden lg:block">
              <div className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="space-y-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-[#2563EB]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">Step 1: Prenota</h3>
                        <p className="text-blue-200 text-sm">Scegli data e orario preferiti</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                        <Users className="h-6 w-6 text-[#2563EB]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">Step 2: Incontra</h3>
                        <p className="text-blue-200 text-sm">Call con i nostri esperti</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                        <Zap className="h-6 w-6 text-[#2563EB]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">Step 3: Inizia</h3>
                        <p className="text-blue-200 text-sm">Implementa la strategia vincente</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vantaggi per Candidati Section */}
      <section 
        id="candidati" 
        className={`py-20 px-6 bg-gradient-to-br from-blue-50 to-white transition-all duration-1000 ${isVisible.candidati ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20">
              Per i Candidati
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Perché i candidati scelgono la nostra consulenza
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Una call di 10 minuti per scoprire le agenzie perfette per te e accedere a opportunità esclusive
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left features for candidates */}
            <div className="space-y-8">
              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gradient-to-br from-[#2563EB] to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Target className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Accesso a agenzie selezionate</h3>
                  <p className="text-gray-600">Ti presentiamo solo agenzie verificate nella tua zona con reali opportunità di crescita</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <MessageCircle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Supporto personalizzato</h3>
                  <p className="text-gray-600">Ti affianchiamo nel processo di selezione con consigli e preparazione ai colloqui</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Creiamo insieme il tuo profilo</h3>
                  <p className="text-gray-600">Ti aiutiamo a creare un profilo che mette in luce quello che sai fare e attira le migliori agenzie</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Formazione e mentoring esclusivi</h3>
                  <p className="text-gray-600">Accesso a risorse premium e sessioni di coaching per accelerare la tua carriera</p>
                </div>
              </div>
            </div>

            {/* Right phone mockup - candidate view */}
            <div className="flex justify-center">
              <PhoneMockup className="phone-3d hover:scale-105">
                <div className="p-6 h-full bg-gradient-to-b from-blue-50 to-white">
                  <div className="text-center mb-6">
                    <h3 className="font-bold text-gray-800 text-lg mb-2">Vista Candidato</h3>
                    <p className="text-sm text-gray-600">Il tuo profilo personale</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                        <span className="text-sm font-medium text-gray-800">5 Nuovi Match</span>
                      </div>
                      <p className="text-xs text-gray-500">Agenzie compatibili nella tua zona</p>
                    </div>
                    
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                      <div className="flex items-center space-x-3 mb-2">
                        <Calendar className="h-4 w-4 text-[#2563EB]" />
                        <span className="text-sm font-medium text-gray-800">3 Colloqui programmati</span>
                      </div>
                      <p className="text-xs text-gray-500">Questa settimana</p>
                    </div>
                    
                    <div className="bg-gradient-to-r from-[#2563EB]/10 to-[#7C3AED]/10 p-4 rounded-xl border border-blue-100">
                      <div className="flex items-center space-x-3 mb-2">
                        <BarChart3 className="h-4 w-4 text-[#7C3AED]" />
                        <span className="text-sm font-medium text-gray-800">Profilo completo al 95%</span>
                      </div>
                      <p className="text-xs text-gray-500">Ottima visibilità per le agenzie</p>
                    </div>
                  </div>
                </div>
              </PhoneMockup>
            </div>
          </div>
          
          {/* CTA per Candidati */}
          <div className="text-center mt-16">
            <Button 
              asChild
              size="lg" 
              className="bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white px-8 py-4 rounded-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 font-bold"
            >
              <a href="https://app.inleads.it/#/accesso" target="_blank" rel="noopener noreferrer">
                <Zap className="mr-3 h-5 w-5" />
                Registrati come candidato
              </a>
            </Button>
            <p className="text-sm text-gray-600 mt-3">Accesso gratuito • Registrazione immediata</p>
          </div>
        </div>
      </section>

      {/* Vantaggi per Agenzie Section */}
      <section 
        id="agenzie" 
        className={`py-20 px-6 bg-gradient-to-br from-green-50 to-blue-50 transition-all duration-1000 ${isVisible.agenzie ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-green-600/10 text-green-700 border-green-600/20">
              Per le Agenzie
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Perché le agenzie scelgono la nostra demo
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Una dimostrazione personalizzata per scoprire come trovare i candidati giusti e migliorare la ricerca
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left phone mockup - agency view */}
            <div className="flex justify-center lg:order-2">
              <PhoneMockup className="phone-3d hover:scale-105">
                <div className="p-6 h-full bg-gradient-to-b from-green-50 to-white">
                  <div className="text-center mb-6">
                    <h3 className="font-bold text-gray-800 text-lg mb-2">Vista Agenzia</h3>
                    <p className="text-sm text-gray-600">Il tuo controllo ricerca candidati</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 p-4 rounded-xl border border-green-100">
                      <div className="flex items-center space-x-3 mb-2">
                        <MessageCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-gray-800">Candidati pronti per il colloquio</span>
                      </div>
                      <p className="text-xs text-gray-500">Visualizza in tempo reale i profili disponibili e prenota i colloqui direttamente dalla tua dashboard.</p>
                    </div>
                    
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                      <div className="flex items-center space-x-3 mb-2">
                        <Shield className="h-4 w-4 text-[#2563EB]" />
                        <span className="text-sm font-medium text-gray-800">Candidati verificati dal team InLeads</span>
                      </div>
                      <p className="text-xs text-gray-500">Ogni candidato è controllato e validato dal nostro team, per garantire qualità e affidabilità.</p>
                    </div>
                    
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                      <div className="flex items-center space-x-3 mb-2">
                        <Target className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-medium text-gray-800">Match IA: 96%</span>
                      </div>
                      <p className="text-xs text-gray-500">Compatibilità media candidati</p>
                    </div>
                  </div>
                </div>
              </PhoneMockup>
            </div>

            {/* Right features for agencies */}
            <div className="space-y-8 lg:order-1">
              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Zap className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Ti aiutiamo a iniziare</h3>
                  <p className="text-gray-600">Prepariamo insieme la piattaforma per le tue necessità di ricerca candidati</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gradient-to-br from-[#2563EB] to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Formazione del team</h3>
                  <p className="text-gray-600">Il tuo team impara a usare la piattaforma per ottenere i migliori risultati</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <BarChart3 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Lista di candidati già pronti</h3>
                  <p className="text-gray-600">Accesso immediato a candidati già controllati e approvati dal nostro team</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Target className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Consulenza strategica continua</h3>
                  <p className="text-gray-600">Ti supportiamo sempre per migliorare la ricerca di candidati e far crescere il team</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* CTA per Agenzie */}
          <div className="text-center mt-16">
            <Button 
              asChild
              size="lg" 
              className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 font-bold"
            >
              <a href="https://app.inleads.it/#/accesso" target="_blank" rel="noopener noreferrer">
                <Briefcase className="mr-3 h-5 w-5" />
                Registrati come agenzia
              </a>
            </Button>
            <p className="text-sm text-gray-600 mt-3">Demo gratuita • Setup immediato</p>
          </div>
        </div>
      </section>

      {/* Statistiche & Feed Annunci Section */}
      <section 
        id="feed" 
        className={`py-20 px-6 bg-gray-50 transition-all duration-1000 ${isVisible.feed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20">
              Opportunità in Tempo Reale
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Scopri il potere del feed InLeads
            </h2>
            <p className="text-lg text-gray-600 mb-4">
              Ogni giorno centinaia di opportunità e connessioni professionali
            </p>
          </div>

          {/* Statistiche del Feed */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <div className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-[#2563EB] to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Briefcase className="h-6 w-6 text-white" />
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-2">847</div>
              <div className="text-sm text-gray-600">Annunci attivi oggi</div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-2">2.847</div>
              <div className="text-sm text-gray-600">Candidature inviate</div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-2">94%</div>
              <div className="text-sm text-gray-600">Tasso di risposta</div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-2">24h</div>
              <div className="text-sm text-gray-600">Tempo medio match</div>
            </div>
          </div>

          {/* Feed Annunci Reali */}
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left side - Live Feed */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-3 h-3 bg-green-400 rounded-full mr-3 animate-pulse"></div>
                Feed Live - Aggiornato ora
              </h3>
              
              <div className="space-y-4">
                {/* Placeholder Annuncio 1 */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#2563EB] to-blue-600 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">IM</span>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Immobiliare Alpha</div>
                        <div className="text-xs text-gray-500">Milano • Pubblicato 2h fa</div>
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-700 text-xs">URGENTE</Badge>
                  </div>
                  <p className="text-gray-700 mb-4 font-medium">
                    🎯 Cerchiamo 2 agenti immobiliari per espansione zona nord. 
                    Esperienza minima 1 anno, ottime provvigioni + incentivi.
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-4 text-sm text-gray-500">
                      <span>💰 Provvigioni competitive</span>
                      <span>📍 Milano</span>
                    </div>
                    <Button size="sm" className="bg-[#2563EB] hover:bg-blue-600">
                      Visualizza
                    </Button>
                  </div>
                </div>

                {/* Placeholder Annuncio 2 */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">AG</span>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Agenzia Capitale</div>
                        <div className="text-xs text-gray-500">Roma • Pubblicato 4h fa</div>
                      </div>
                    </div>
                    <Badge className="bg-blue-100 text-blue-700 text-xs">TEAM LEADER</Badge>
                  </div>
                  <p className="text-gray-700 mb-4 font-medium">
                    🚀 Opportunità Team Leader per agenzia in crescita. Gestione team, 
                    bonus sui risultati + benefit aziendali.
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-4 text-sm text-gray-500">
                      <span>👥 Gestione Team</span>
                      <span>📍 Roma</span>
                    </div>
                    <Button size="sm" variant="outline" className="border-purple-200 text-purple-600 hover:bg-purple-50">
                      Scopri di più
                    </Button>
                  </div>
                </div>

                {/* Placeholder Annuncio 3 */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">PR</span>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Prime Properties</div>
                        <div className="text-xs text-gray-500">Torino • Pubblicato 6h fa</div>
                      </div>
                    </div>
                    <Badge className="bg-orange-100 text-orange-700 text-xs">NEOLAUREATI</Badge>
                  </div>
                  <p className="text-gray-700 mb-4 font-medium">
                    🎓 Prima esperienza nel real estate? Programma formativo completo + affiancamento 
                    senior. Crescita garantita!
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-4 text-sm text-gray-500">
                      <span>📚 Formazione inclusa</span>
                      <span>📍 Torino</span>
                    </div>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700">
                      Scopri Opportunità
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Valore del Feed */}
            <div className="lg:pl-8">
              <div className="bg-gradient-to-br from-[#2563EB]/10 to-[#7C3AED]/10 rounded-2xl p-8 border border-blue-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Il feed che accelera la tua carriera
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-[#2563EB] rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Opportunità esclusive</h4>
                      <p className="text-gray-600 text-sm">Le migliori offerte vengono pubblicate prima su InLeads, dandoti un vantaggio competitivo</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-[#7C3AED] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Target className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Offerte su misura per te</h4>
                      <p className="text-gray-600 text-sm">Vedi solo annunci adatti a quello che sai fare, alla tua esperienza e alla tua zona</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <BarChart3 className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Trasparenza totale</h4>
                      <p className="text-gray-600 text-sm">Provvigioni, benefit e dettagli chiari fin da subito. Nessuna sorpresa</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Contatto diretto</h4>
                      <p className="text-gray-600 text-sm">Parla direttamente con i titolari delle agenzie, senza intermediari</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-white rounded-lg border border-gray-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#2563EB] mb-1">Oltre 15.000</div>
                    <div className="text-sm text-gray-600">connessioni di successo nel 2024</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={() => scrollToSection('feed-tutorial')}
              size="lg" 
              className="bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white px-8 py-4 text-lg rounded-xl hover:shadow-xl transition-all duration-300"
            >
              Scopri come funziona il feed
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Feed Tutorial Section */}
      <section 
        id="feed-tutorial" 
        className={`py-20 px-6 bg-white transition-all duration-1000 ${isVisible['feed-tutorial'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-500/10 text-orange-600 border-orange-500/20">
              Come funziona il Feed
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Il tuo feed personalizzato ti guida al successo
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Scopri come il feed InLeads ti tiene aggiornato su offerte di lavoro, consigli e contatti utili
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Left side - Feed mockup */}
            <div className="flex justify-center">
              <PhoneMockup className="phone-3d hover:scale-105">
                <div className="p-4 h-full bg-gradient-to-b from-orange-50 to-white">
                  <div className="text-center mb-4">
                    <h3 className="font-bold text-gray-800 text-base mb-1">Feed InLeads</h3>
                    <p className="text-xs text-gray-600">Il tuo network immobiliare</p>
                  </div>
                  
                  <div className="space-y-3 text-xs">
                    {/* Feed Post 1 */}
                    <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-[#2563EB] to-[#7C3AED] rounded-full"></div>
                        <div>
                          <div className="font-medium text-gray-800 text-xs">Immobiliare Milano</div>
                          <div className="text-gray-500 text-xs">2 ore fa</div>
                        </div>
                      </div>
                      <p className="text-gray-700 text-xs mb-2">🎯 Cerchiamo 2 agenti per zona Brera. Esperienza min 1 anno.</p>
                      <div className="flex space-x-2">
                        <button className="bg-[#2563EB] text-white px-2 py-1 rounded text-xs">Candidati</button>
                        <button className="text-gray-600 text-xs">💬 3</button>
                        <button className="text-gray-600 text-xs">❤️ 12</button>
                      </div>
                    </div>

                    {/* Feed Post 2 */}
                    <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-green-600 rounded-full"></div>
                        <div>
                          <div className="font-medium text-gray-800 text-xs">Marco R. - Agente</div>
                          <div className="text-gray-500 text-xs">4 ore fa</div>
                        </div>
                      </div>
                      <p className="text-gray-700 text-xs mb-2">📚 Consiglio: Come preparare al meglio un colloquio in agenzia</p>
                      <div className="flex space-x-2">
                        <button className="text-gray-600 text-xs">📖 Leggi tutto</button>
                        <button className="text-gray-600 text-xs">💬 8</button>
                        <button className="text-gray-600 text-xs">❤️ 25</button>
                      </div>
                    </div>

                    {/* Feed Post 3 */}
                    <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full"></div>
                        <div>
                          <div className="font-medium text-gray-800 text-xs">InLeads</div>
                          <div className="text-gray-500 text-xs">1 giorno fa</div>
                        </div>
                      </div>
                      <p className="text-gray-700 text-xs mb-2">🚀 Webinar gratuito: "Tecniche di vendita immobiliare 2024"</p>
                      <div className="flex space-x-2">
                        <button className="bg-orange-500 text-white px-2 py-1 rounded text-xs">Partecipa</button>
                        <button className="text-gray-600 text-xs">💬 15</button>
                        <button className="text-gray-600 text-xs">❤️ 45</button>
                      </div>
                    </div>
                  </div>
                </div>
              </PhoneMockup>
            </div>

            {/* Right side - Tutorial steps */}
            <div className="space-y-8">
              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gradient-to-br from-[#2563EB] to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-white font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Opportunità in tempo reale</h3>
                  <p className="text-gray-600">Le agenzie pubblicano offerte di lavoro, ricerche di candidati e opportunità di collaborazione direttamente nel feed</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-white font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Contenuti formativi esclusivi</h3>
                  <p className="text-gray-600">Agenti esperti condividono consigli, best practice e strategie di successo per aiutare la community a crescere</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-white font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Eventi e formazione InLeads</h3>
                  <p className="text-gray-600">Corsi online, formazione gratuita e incontri con altri agenti per crescere insieme</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-white font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Fai domande e conosci altri</h3>
                  <p className="text-gray-600">Commenta, chiedi consigli e costruisci amicizie con altri agenti immobiliari</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Il feed si adatta a te</h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              L'algoritmo InLeads personalizza il tuo feed basandosi sui tuoi interessi, zona geografica e obiettivi professionali. 
              Più interagisci, più il feed diventa rilevante per la tua carriera.
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="text-2xl mb-2">🎯</div>
                <div className="font-semibold text-gray-800 mb-1">Personalizzato</div>
                <div className="text-gray-600">Contenuti su misura per te</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="text-2xl mb-2">⚡</div>
                <div className="font-semibold text-gray-800 mb-1">In tempo reale</div>
                <div className="text-gray-600">Aggiornamenti immediati</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="text-2xl mb-2">🤝</div>
                <div className="font-semibold text-gray-800 mb-1">Community-driven</div>
                <div className="text-gray-600">Creato dalla community per la community</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pacchetti Section */}
      <section 
        id="pacchetti"
        className={`py-20 px-6 bg-white transition-all duration-1000 ${isVisible.pacchetti ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20">
              I Nostri Pacchetti
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Soluzioni su Misura per Ogni Agenzia
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Che tu stia iniziando o abbia già un team strutturato, InLeads ha la soluzione giusta per le tue esigenze di reclutamento.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Start Zone Package */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#2563EB] hover:shadow-xl transition-all duration-300 group">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Start Zone</h3>
                <p className="text-gray-600">Per agenzie che iniziano</p>
              </div>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Profili senza esperienza da formare</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">3 candidati garantiti al mese</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Supporto nella formazione iniziale</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Mentoring personalizzato</span>
                </div>
              </div>
              
              <Button 
                asChild
                className="w-full bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
              >
                <a
                  href={`https://calendly.com/inleads-info/30min?month=${new Date().toISOString().slice(0,7)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Prenota una consulenza
                </a>
              </Button>
            </div>

            {/* Flusso Attivo Package */}
            <div className="bg-gradient-to-br from-[#2563EB] to-[#7C3AED] text-white rounded-2xl p-8 transform scale-105 shadow-2xl relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <Badge className="bg-white/20 text-white border-white/30 text-xs">
                  PIÙ POPOLARE
                </Badge>
              </div>
              
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Flusso Attivo</h3>
                <p className="text-blue-100">Per agenzie in crescita</p>
              </div>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-300 mt-0.5 flex-shrink-0" />
                  <span className="text-blue-100">Profili con minima esperienza</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-300 mt-0.5 flex-shrink-0" />
                  <span className="text-blue-100">5 candidati qualificati al mese</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-300 mt-0.5 flex-shrink-0" />
                  <span className="text-blue-100">Screening avanzato dei profili</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-300 mt-0.5 flex-shrink-0" />
                  <span className="text-blue-100">Supporto continuativo</span>
                </div>
              </div>
              
              <Button 
                asChild
                className="w-full bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
              >
                <a
                  href={`https://calendly.com/inleads-info/30min?month=${new Date().toISOString().slice(0,7)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Prenota una consulenza
                </a>
              </Button>
            </div>

            {/* Team Esperto Package */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#7C3AED] hover:shadow-xl transition-all duration-300 group">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#7C3AED] to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Team Esperto</h3>
                <p className="text-gray-600">Per agenzie strutturate</p>
              </div>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Profili con 3+ anni di esperienza</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">3 colloqui garantiti al mese</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Profili senior e team leader</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Consulenza strategica avanzata</span>
                </div>
              </div>
              
              <Button 
                asChild
                className="w-full bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
              >
                <a
                  href={`https://calendly.com/inleads-info/30min?month=${new Date().toISOString().slice(0,7)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Prenota una consulenza
                </a>
              </Button>
            </div>
          </div>

          {/* CTA Bottom */}
          <div className="text-center mt-16">
            <p className="text-gray-600 mb-6">
              Non sai quale pacchetto fa per te? Contattaci direttamente!
            </p>
            <Button 
              asChild
              size="lg"
              className="bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white px-8 py-4 rounded-xl font-bold hover:shadow-xl transition-all duration-300"
            >
              <a
                href={`https://calendly.com/inleads-info/30min?month=${new Date().toISOString().slice(0,7)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Prenota una consulenza
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Download CTA Section */}
      <section 
        id="download" 
        className={`py-20 px-6 bg-gradient-to-br from-[#2563EB] via-[#7C3AED] to-purple-700 text-white relative overflow-hidden transition-all duration-1000 ${isVisible.download ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        {/* Background waves */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute animate-wave">
            <svg className="w-full h-32" viewBox="0 0 1200 120" preserveAspectRatio="none">
              <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" className="fill-white"></path>
            </svg>
          </div>
        </div>
        
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
            Parlare con il nostro team richiede meno di 10 minuti, ma può farti risparmiare settimane di tempo
          </h2>
          <p className="text-xl mb-12 text-blue-100">
            La piattaforma che unisce tecnologia e persone vere per trovare lavoro nel settore immobiliare.
          </p>
          
          <div className="flex justify-center mb-12 w-full max-w-md mx-auto">
            <Button 
              asChild
              size="lg" 
              className="bg-white text-[#2563EB] px-8 py-4 text-lg rounded-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 font-bold group w-full"
            >
              <a href="https://app.inleads.it/#/accesso" target="_blank" rel="noopener noreferrer">
                <Zap className="mr-3 h-6 w-6 group-hover:scale-110 transition-transform duration-200" />
                Attiva il tuo account ora
              </a>
            </Button>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 text-center max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Accesso completo alla piattaforma InLeads</h3>
            <p className="text-blue-100 mb-6">Una consulenza di 10 minuti per configurare il tuo account e iniziare subito</p>
            
            <div className="grid md:grid-cols-2 gap-6 text-left mb-6">
              <div>
                <h4 className="font-semibold text-white mb-2 flex items-center">
                  <Users className="mr-2 h-4 w-4" />
                  Candidati
                </h4>
                <ul className="text-blue-200 text-sm space-y-1">
                  <li>• Profilo ben fatto</li>
                  <li>• Match con agenzie</li>
                  <li>• Accesso al feed</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-white mb-2 flex items-center">
                  <Briefcase className="mr-2 h-4 w-4" />
                  Agenzie
                </h4>
                <ul className="text-blue-200 text-sm space-y-1">
                  <li>• Il tuo pannello di controllo</li>
                  <li>• Lista di tutti i candidati</li>
                  <li>• I tuoi numeri e risultati</li>
                </ul>
              </div>
            </div>

            <div className="text-blue-200 text-sm">
              📅 Inizi subito • 🎯 Consulenza gratuita • ⚡ Attivo in giornata
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {/* Logo & Info */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src="/logo-originale.png"
                  alt="InLeads Logo"
                  className="h-10 w-auto brightness-0 invert"
                />
                <span className="text-2xl font-bold">InLeads</span>
              </div>
              <p className="text-gray-300 leading-relaxed mb-6 max-w-md">
                La piattaforma che connette agenzie e talenti nel settore immobiliare.
              </p>
              
              <div className="space-y-2 mb-6">
                <p className="text-gray-300">
                  📧 <a href="mailto:info@inleads.it" className="hover:text-white transition-colors">info@inleads.it</a>
                </p>
                <p className="text-gray-300">
                  🌐 <a href="https://www.inleads.it" className="hover:text-white transition-colors">www.inleads.it</a>
                </p>
              </div>

              {/* Social Links */}
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-[#2563EB] transition-colors">
                  <span className="text-sm font-bold">Li</span>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-pink-600 transition-colors">
                  <span className="text-sm font-bold">Ig</span>
                </a>
              </div>
            </div>

            {/* Links */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Piattaforma</h3>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Come funziona</a></li>
                <li><a href="/blog" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Funzionalità</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Demo gratuita</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Supporto</h3>
              <ul className="space-y-3">
                <li><a href="/contatti" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Contatti</a></li>
                <li><a href="/formazione" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Formazione</a></li>
                <li><a href="/privacy" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Privacy Policy</a></li>
                <li><a href="https://app.inleads.it/#/demo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Demo</a></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between text-sm text-gray-400">
            <p>&copy; 2025 InLeads. Tutti i diritti riservati.</p>
            <div className="mt-4 md:mt-0 flex space-x-6">
              <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Termini di Servizio</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Book Call Modal */}
      <BookCallModal 
        isOpen={showBookCallModal} 
        onClose={() => setShowBookCallModal(false)} 
      />
    </div>
  );
};

export default ShowcaseLanding;